import React, { useEffect, useRef, useCallback, useState } from "react"
import { useLocation } from "react-router-dom"
import PropTypes from "prop-types"

// Import Scrollbar
import SimpleBar from "simplebar-react"
import withRouter from "components/Common/withRouter"
import { Link } from "react-router-dom"

//i18n
import { withTranslation } from "react-i18next"

const SidebarContent = props => {
  const ref = useRef()
  const [roleName, setRoleName] = useState("USER")
  const [activePath, setActivePath] = useState("")

  // Update role when sessionStorage changes - listen to storage events
  useEffect(() => {
    const updateRole = () => {
      const currentRole = sessionStorage.getItem("roleName") || "USER"
      setRoleName(currentRole)
    }

    // Update role immediately on mount
    updateRole()

    // Listen for storage changes (useful when tabs sync)
    window.addEventListener("storage", updateRole)

    // Poll for role changes with aggressive timing on mount
    const quickInterval = setInterval(() => {
      updateRole()
    }, 100)

    // After 3 seconds, reduce polling frequency
    const timeout = setTimeout(() => {
      clearInterval(quickInterval)
      const slowInterval = setInterval(() => {
        updateRole()
      }, 500)
      
      // After 10 more seconds, stop polling
      setTimeout(() => {
        clearInterval(slowInterval)
      }, 10000)
      
      return () => clearInterval(slowInterval)
    }, 3000)

    return () => {
      clearInterval(quickInterval)
      clearTimeout(timeout)
      window.removeEventListener("storage", updateRole)
    }
  }, [])

  const path = useLocation()

  // Update active path when location changes
  useEffect(() => {
    setActivePath(path.pathname)
  }, [path.pathname])

  useEffect(() => {
    ref.current.recalculate()
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" })
  }, [activePath])

  // Helper function to check if a menu item is active
  const isActive = (path) => {
    return activePath === path
  }

  // Helper function to check if a parent menu should be highlighted
  const isParentActive = (childPaths) => {
    return childPaths.some(childPath => activePath === childPath)
  }

  return (
    <React.Fragment>
      <SimpleBar className="h-100" ref={ref}>
        <div id="sidebar-menu">
          <ul className="sidebar-menu list-unstyled" id="side-menu">
            <li className="menu-title">{props.t("Menu")}</li>

            {roleName === "ADMIN" ? (
              // Admin Menu Structure
              <>
                <li className={`menu-item ${isActive("/AdminDashboard") ? "active" : ""}`}>
                  <Link to="/AdminDashboard" className="menu-link">
                    <i className="bx bx-home-circle"></i>
                    <span>{props.t("Dashboards")}</span>
                  </Link>
                </li>

                <li className={`menu-item ${isParentActive(["/AdminReport/AdminSummeryReport", "/ReportsV2/admin-campaign-reports", "/ReportsV2/admin-detailed-report", "/ReportsV2/admin-scheduled-campaigns"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-spreadsheet"></i>
                    <span>{props.t("Reports")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/AdminReport/AdminSummeryReport") ? "active" : ""}`}>
                      <Link to="/AdminReport/AdminSummeryReport">{props.t("Summary")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/ReportsV2/admin-campaign-reports") ? "active" : ""}`}>
                      <Link to="/ReportsV2/admin-campaign-reports">{props.t("Campaign Reports")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/ReportsV2/admin-detailed-report") ? "active" : ""}`}>
                      <Link to="/ReportsV2/admin-detailed-report">{props.t("Detailed Messages")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/ReportsV2/admin-scheduled-campaigns") ? "active" : ""}`}>
                      <Link to="/ReportsV2/admin-scheduled-campaigns">{props.t("Scheduled")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/AdminReport/OutMessages") ? "active" : ""}`}>
                      <Link to="/AdminReport/OutMessages">{props.t("Out Messages")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Admin/users", "/Admin/resellers", "/Admin/purchase-transactions", "/Admin/payment-transactions", "/AdminManage/adminGateway", "/Admin/context-notes"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-group"></i>
                    <span>{props.t("Accounts")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Admin/users") ? "active" : ""}`}>
                      <Link to="/Admin/users">{props.t("User")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/resellers") ? "active" : ""}`}>
                      <Link to="/Admin/resellers">{props.t("Resellers")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/purchase-transactions") ? "active" : ""}`}>
                      <Link to="/Admin/purchase-transactions">{props.t("Sales")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/payment-transactions") ? "active" : ""}`}>
                      <Link to="/Admin/payment-transactions">{props.t("Payments")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/AdminManage/adminGateway") ? "active" : ""}`}>
                      <Link to="/AdminManage/adminGateway">{props.t("Gateways")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/context-notes") ? "active" : ""}`}>
                      <Link to="/Admin/context-notes">{props.t("Context Notes")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Admin/adminquicksms", "/Admin/adminpersonalizedsms", "/Admin/admincustomizedsms", "/Admin/adminbulksms"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-send"></i>
                    <span>{props.t("SMS")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Admin/validatetemplate") ? "active" : ""}`}>
                    <Link to="/Admin/validatetemplate">{props.t("Validate Template")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/adminquicksms") ? "active" : ""}`}>
                      <Link to="/Admin/adminquicksms">{props.t("Quick SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/adminpersonalizedsms") ? "active" : ""}`}>
                      <Link to="/Admin/adminpersonalizedsms">{props.t("Personalize SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/admincustomizedsms") ? "active" : ""}`}>
                      <Link to="/Admin/admincustomizedsms">{props.t("Customize SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/adminbulksms") ? "active" : ""}`}>
                      <Link to="/Admin/adminbulksms">{props.t("Bulk SMS")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/AdminManage/adminsenderid", "/Admin/admindlttemplates", "/Admin/admingroup"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-customize"></i>
                    <span>{props.t("AdminManage")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/AdminManage/adminsenderid") ? "active" : ""}`}>
                      <Link to="/AdminManage/adminsenderid">{props.t("Sender Ids")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/admindlttemplates") ? "active" : ""}`}>
                      <Link to="/Admin/admindlttemplates">{props.t("Dlt Templates")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Admin/admingroup") ? "active" : ""}`}>
                      <Link to="/Admin/admingroup">{props.t("Groups")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/AdminReport/Adminbilling/usermetrics", "/AdminReport/Adminbilling/dlr-status-summary"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-customize"></i>
                    <span>{props.t("Billing")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/AdminReport/Adminbilling/usermetrics") ? "active" : ""}`}>
                      <Link to="/AdminReport/Adminbilling/usermetrics">{props.t("User Metrics")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/AdminReport/Adminbilling/dlr-status-summary") ? "active" : ""}`}>
                      <Link to="/AdminReport/Adminbilling/dlr-status-summary">{props.t("DLR Status Summary")}</Link>
                    </li>
                    
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Admin/menus"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-cog"></i>
                    <span>{props.t("App Settings")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Admin/menus") ? "active" : ""}`}>
                      <Link to="/Admin/menus">{props.t("Menus")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/crm/dashboard", "/crm/customers", "/crm/categories"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-building-house"></i>
                    <span>{props.t("CRM")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/crm/dashboard") ? "active" : ""}`}>
                      <Link to="/crm/dashboard">{props.t("Dashboard")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/crm/customers") ? "active" : ""}`}>
                      <Link to="/crm/customers">{props.t("Customers")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/crm/categories") ? "active" : ""}`}>
                      <Link to="/crm/categories">{props.t("Categories")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/crm/purchases") ? "active" : ""}`}>
                      <Link to="/crm/purchases">{props.t("Purchases")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/crm/payments") ? "active" : ""}`}>
                      <Link to="/crm/payments">{props.t("Payments")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/crm/ledger") ? "active" : ""}`}>
                      <Link to="/crm/ledger">{props.t("Ledger")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/WhatsApp/campaigns", "/WhatsApp/templates", "/WhatsApp/waba-numbers"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bxl-whatsapp"></i>
                    <span>{props.t("WhatsApp")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/WhatsApp/campaigns") ? "active" : ""}`}>
                      <Link to="/WhatsApp/campaigns">{props.t("Campaigns")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/WhatsApp/templates") ? "active" : ""}`}>
                      <Link to="/WhatsApp/templates">{props.t("Templates")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/WhatsApp/waba-numbers") ? "active" : ""}`}>
                      <Link to="/WhatsApp/waba-numbers">{props.t("WABA Numbers")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/RCS/campaigns", "/RCS/templates"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-message-rounded-dots"></i>
                    <span>{props.t("RCS")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/RCS/campaigns") ? "active" : ""}`}>
                      <Link to="/RCS/campaigns">{props.t("Campaigns")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/RCS/templates") ? "active" : ""}`}>
                      <Link to="/RCS/templates">{props.t("Templates")}</Link>
                    </li>
                  </ul>
                </li>
              </>
            ) : roleName === "RESELLER" ? (
              // RESELLER Menu Structure
              <>
                <li className={`menu-item ${isActive("/#") ? "active" : ""}`}>
                  <Link to="/#" className="menu-link">
                    <i className="bx bx-home-circle"></i>
                    <span>{props.t("Dashboards")}</span>
                  </Link>
                </li>

                <li className={`menu-item ${isParentActive(["/Manage/Sendersid", "/Manage/Template", "/Manage/Groups", "/Manage/ValidateTemplate"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-store"></i>
                    <span>{props.t("Manage")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Manage/Sendersid") ? "active" : ""}`}>
                      <Link to="/Manage/Sendersid">{props.t("Sender Ids")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Manage/Template") ? "active" : ""}`}>
                      <Link to="/Manage/Template">{props.t("Templates")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Manage/Groups") ? "active" : ""}`}>
                      <Link to="/Manage/Groups">{props.t("Groups")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Manage/ValidateTemplate") ? "active" : ""}`}>
                      <Link to="/Manage/ValidateTemplate">{props.t("Validate Template")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Sms/quicksms", "/Sms/customizedSms", "/Sms/personalizedSms", "/Sms/bulkSms"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-bitcoin"></i>
                    <span>{props.t("SMS")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Sms/quicksms") ? "active" : ""}`}>
                      <Link to="/Sms/quicksms">{props.t("Quick SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Sms/customizedSms") ? "active" : ""}`}>
                      <Link to="/Sms/customizedSms">{props.t("Customize SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Sms/personalizedSms") ? "active" : ""}`}>
                      <Link to="/Sms/personalizedSms">{props.t("Personalize SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Sms/bulkSms") ? "active" : ""}`}>
                      <Link to="/Sms/bulkSms">{props.t("Bulk SMS")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Reports/senderSummaryReport", "/ReportsV2/campaigns", "/ReportsV2/scheduled-campaigns", "/ReportsV2/detailed-search", "/Reports/short-url-summary"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-envelope"></i>
                    <span>{props.t("Reports")}</span>
                  </div>
                  <ul className="sub-menu">
                  <li className={`sub-menu-item ${isActive("/Reports/senderSummaryReport") ? "active" : ""}`}>
                      <Link to="/Reports/senderSummaryReport">{props.t("Summary ")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/ReportsV2/campaigns") ? "active" : ""}`}>
                      <Link to="/ReportsV2/campaigns">{props.t("Campaign Report")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/ReportsV2/scheduled-campaigns") ? "active" : ""}`}>
                      <Link to="/ReportsV2/scheduled-campaigns">{props.t("Scheduled Report")}</Link>
                    </li>
                     <li className={`sub-menu-item ${isActive("/ReportsV2/detailed-search") ? "active" : ""}`}>
                      <Link to="/ReportsV2/detailed-search">{props.t("Detailed Search")}</Link>
                    </li>
                     <li className={`sub-menu-item ${isActive("/Reports/short-url-summary") ? "active" : ""}`}>
                      <Link to="/Reports/short-url-summary">{props.t("ShortUrl Campaigns")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Accounts/purchases", "/Accounts/payments", "/Accounts/sales", "/Accounts/statistics"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-user"></i>
                    <span>{props.t("Accounts")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Accounts/purchases") ? "active" : ""}`}>
                      <Link to="/Accounts/purchases">{props.t("Purchases")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Accounts/payments") ? "active" : ""}`}>
                      <Link to="/Accounts/payments">{props.t("Payments")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Accounts/sales") ? "active" : ""}`}>
                      <Link to="/Accounts/sales">{props.t("Sales")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Accounts/statistics") ? "active" : ""}`}>
                      <Link to="/Accounts/statistics">{props.t("Statistics")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Setting/api"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-book-bookmark"></i>
                    <span>{props.t("Settings")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Setting/api") ? "active" : ""}`}>
                      <Link to="/Setting/api">{props.t("Api")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Setting/ip-whitelisting") ? "active" : ""}`}>
                      <Link to="/Setting/ip-whitelisting">{props.t("IP Whitelisting")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Setting/blocklist-numbers") ? "active" : ""}`}>
                      <Link to="/Setting/blocklist-numbers">{props.t("Blocklist Numbers")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Setting/whitelist-numbers") ? "active" : ""}`}>
                      <Link to="/Setting/whitelist-numbers">{props.t("Whitelist Numbers")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Reseller/dashboard", "/Reseller/ManageClients", "/Reseller/reports/Summary", "/Reseller/credits-transfer"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-group"></i>
                    <span>{props.t("Reseller")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Reseller/dashboard") ? "active" : ""}`}>
                      <Link to="/Reseller/dashboard">{props.t("Dashboard")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Reseller/ManageClients") ? "active" : ""}`}>
                      <Link to="/Reseller/ManageClients">{props.t("Client List")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Reseller/credits-transfer") ? "active" : ""}`}>
                      <Link to="/Reseller/credits-transfer">{props.t("Credits Transfer")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Reseller/reports/Summary") ? "active" : ""}`}>
                      <Link to="/Reseller/reports/Summary">{props.t("Summary")}</Link>
                    </li>
                    {/* <li className={`sub-menu-item ${isActive("/Reseller/reports/detailed") ? "active" : ""}`}>
                      <Link to="/Reseller/reports/detailed">{props.t("Detailed")}</Link>
                    </li> */}
                  </ul>
                </li>

                {/* <li className={`menu-item ${isParentActive(["/WhatsApp/campaigns", "/WhatsApp/templates", "/WhatsApp/waba-numbers"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bxl-whatsapp"></i>
                    <span>{props.t("WhatsApp")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/WhatsApp/campaigns") ? "active" : ""}`}>
                      <Link to="/WhatsApp/campaigns">{props.t("Campaigns")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/WhatsApp/templates") ? "active" : ""}`}>
                      <Link to="/WhatsApp/templates">{props.t("Templates")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/WhatsApp/waba-numbers") ? "active" : ""}`}>
                      <Link to="/WhatsApp/waba-numbers">{props.t("WABA Numbers")}</Link>
                    </li>
                  </ul>
                </li> */}

                {/* <li className={`menu-item ${isParentActive(["/RCS/campaigns", "/RCS/templates"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-message-rounded-dots"></i>
                    <span>{props.t("RCS")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/RCS/campaigns") ? "active" : ""}`}>
                      <Link to="/RCS/campaigns">{props.t("Campaigns")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/RCS/templates") ? "active" : ""}`}>
                      <Link to="/RCS/templates">{props.t("Templates")}</Link>
                    </li>
                  </ul>
                </li> */}
              </>
            ) : (
              // USER Menu Structure
              <>
                <li className={`menu-item ${isActive("/#") ? "active" : ""}`}>
                  <Link to="/#" className="menu-link">
                    <i className="bx bx-home-circle"></i>
                    <span>{props.t("Dashboards")}</span>
                  </Link>
                </li>

                <li className={`menu-item ${isParentActive(["/Manage/Sendersid", "/Manage/Template", "/Manage/Groups", "/Manage/ValidateTemplate"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-store"></i>
                    <span>{props.t("Manage")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Manage/Sendersid") ? "active" : ""}`}>
                      <Link to="/Manage/Sendersid">{props.t("Sender Ids")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Manage/Template") ? "active" : ""}`}>
                      <Link to="/Manage/Template">{props.t("DLT Templates")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Manage/Groups") ? "active" : ""}`}>
                      <Link to="/Manage/Groups">{props.t("Groups")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Manage/ValidateTemplate") ? "active" : ""}`}>
                      <Link to="/Manage/ValidateTemplate">{props.t("Validate Template")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Sms/quicksms", "/Sms/customizedSms", "/Sms/personalizedSms", "/Sms/bulkSms"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-bitcoin"></i>
                    <span>{props.t("SMS")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Sms/quicksms") ? "active" : ""}`}>
                      <Link to="/Sms/quicksms">{props.t("Quick SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Sms/customizedSms") ? "active" : ""}`}>
                      <Link to="/Sms/customizedSms">{props.t("Customize SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Sms/personalizedSms") ? "active" : ""}`}>
                      <Link to="/Sms/personalizedSms">{props.t("Personalize SMS")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Sms/bulkSms") ? "active" : ""}`}>
                      <Link to="/Sms/bulkSms">{props.t("Bulk SMS")}</Link>
                    </li>
                  </ul>
                </li>

                {/* <li className={`menu-item ${isParentActive(["/WhatsApp/campaigns", "/WhatsApp/templates", "/WhatsApp/waba-numbers"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bxl-whatsapp"></i>
                    <span>{props.t("WhatsApp")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/WhatsApp/campaigns") ? "active" : ""}`}>
                      <Link to="/WhatsApp/campaigns">{props.t("Campaigns")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/WhatsApp/templates") ? "active" : ""}`}>
                      <Link to="/WhatsApp/templates">{props.t("Templates")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/WhatsApp/waba-numbers") ? "active" : ""}`}>
                      <Link to="/WhatsApp/waba-numbers">{props.t("WABA Numbers")}</Link>
                    </li>
                  </ul>
                </li> */}

                {/* <li className={`menu-item ${isParentActive(["/RCS/campaigns", "/RCS/templates"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-message-rounded-dots"></i>
                    <span>{props.t("RCS")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/RCS/campaigns") ? "active" : ""}`}>
                      <Link to="/RCS/campaigns">{props.t("Campaigns")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/RCS/templates") ? "active" : ""}`}>
                      <Link to="/RCS/templates">{props.t("Templates")}</Link>
                    </li>
                  </ul>
                </li> */}

                <li className={`menu-item ${isParentActive(["/Reports/senderSummaryReport", "/ReportsV2/campaigns", "/Reports/short-url-summary", "/ReportsV2/detailed-search",  "/ReportsV2/scheduled-campaigns"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-envelope"></i>
                    <span>{props.t("Reports")}</span>
                  </div>
                  <ul className="sub-menu">
                    {/* <li className={`sub-menu-item ${isActive("/Reports/summaryReport") ? "active" : ""}`}>
                      <Link to="/Reports/summaryReport">{props.t("Summary")}</Link>
                    </li> */}
                    <li className={`sub-menu-item ${isActive("/Reports/senderSummaryReport") ? "active" : ""}`}>
                      <Link to="/Reports/senderSummaryReport">{props.t("Summary ")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/ReportsV2/campaigns") ? "active" : ""}`}>
                      <Link to="/ReportsV2/campaigns">{props.t("Campaign Report")}</Link>
                    </li>
                    {/* <li className={`sub-menu-item ${isActive("/Reports/apiSmsReport") ? "active" : ""}`}>
                      <Link to="/Reports/apiSmsReport">{props.t("Api Detailed Report")}</Link>
                    </li> */}
                    {/* <li className={`sub-menu-item ${isActive("/Reports/apiDetailedReport") ? "active" : ""}`}>
                      <Link to="/Reports/apiDetailedReport">{props.t("Api Search Messages")}</Link>
                    </li> */}
                    <li className={`sub-menu-item ${isActive("/ReportsV2/scheduled-campaigns") ? "active" : ""}`}>
                      <Link to="/ReportsV2/scheduled-campaigns">{props.t("Scheduled Report")}</Link>
                    </li>
                     <li className={`sub-menu-item ${isActive("/ReportsV2/detailed-search") ? "active" : ""}`}>
                      <Link to="/ReportsV2/detailed-search">{props.t("Detailed Search")}</Link>
                    </li>
                     <li className={`sub-menu-item ${isActive("/Reports/short-url-summary") ? "active" : ""}`}>
                      <Link to="/Reports/short-url-summary">{props.t("ShortUrl Campaigns")}</Link>
                    </li>
                     {/* <li className={`sub-menu-item ${isActive("/Reports/apiScheduledReport") ? "active" : ""}`}>
                      <Link to="/Reports/apiScheduledReport">{props.t("API Scheduled Report")}</Link>
                    </li> */}
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Accounts/payments", "/Accounts/resellers", "/Accounts/sales", "/Accounts/purchases", "/Accounts/statistics"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-user"></i>
                    <span>{props.t("Accounts")}</span>
                  </div>
                  <ul className="sub-menu">
                    {/* <li className={`sub-menu-item ${isActive("/Accounts/payments") ? "active" : ""}`}>
                      <Link to="/Accounts/payments">{props.t("Payments")}</Link>
                    </li> */}
                    {/* <li className={`sub-menu-item ${isActive("/Accounts/resellers") ? "active" : ""}`}>
                      <Link to="/Accounts/resellers">{props.t("Resellers")}</Link>
                    </li> */}
                    {/* <li className={`sub-menu-item ${isActive("/Accounts/sales") ? "active" : ""}`}>
                      <Link to="/Accounts/sales">{props.t("Sales")}</Link>
                    </li> */}
                    <li className={`sub-menu-item ${isActive("/Accounts/purchases") ? "active" : ""}`}>
                      <Link to="/Accounts/purchases">{props.t("Purchases")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Accounts/statistics") ? "active" : ""}`}>
                      <Link to="/Accounts/statistics">{props.t("Statistics")}</Link>
                    </li>
                  </ul>
                </li>

                <li className={`menu-item ${isParentActive(["/Setting/api"]) ? "parent-active" : ""}`}>
                  <div className="menu-parent">
                    <i className="bx bx-book-bookmark"></i>
                    <span>{props.t("Settings")}</span>
                  </div>
                  <ul className="sub-menu">
                    <li className={`sub-menu-item ${isActive("/Setting/api") ? "active" : ""}`}>
                      <Link to="/Setting/api">{props.t("Api")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Setting/ip-whitelisting") ? "active" : ""}`}>
                      <Link to="/Setting/ip-whitelisting">{props.t("IP Whitelisting")}</Link>
                    </li>
                    <li className={`sub-menu-item ${isActive("/Setting/blocklist-numbers") ? "active" : ""}`}>
                      <Link to="/Setting/blocklist-numbers">{props.t("Blocklist Numbers")}</Link>
                    </li>
                    {/* <li className={`sub-menu-item ${isActive("/Setting/whitelist-numbers") ? "active" : ""}`}>
                      <Link to="/Setting/whitelist-numbers">{props.t("Whitelist Numbers")}</Link>
                    </li> */}
                  </ul>
                </li>
              </>
            )}
          </ul>
        </div>
      </SimpleBar>
    </React.Fragment>
  );
}

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
}

export default withRouter(withTranslation()(SidebarContent))
